import { Query } from "@apollo/client/react/components";
import * as palette from "@govlaunch/palette";
import get from "lodash/fp/get";
import { WithRouterProps } from "next/dist/client/with-router";
import Link from "next/link";
import { withRouter } from "next/router";
import React from "react";
import { FormattedMessage } from "react-intl";
import ConnectQuery from "~/lib/queries/ConnectQuery";
import { IConnectQuery, IConnectQueryVariables } from "~/lib/queries/__generated__/ConnectQuery.generated";
import * as Types from "~/types/types";
import { useSelfie } from "~/components/auth/Auth";
import { NoPosts } from "~/components/profile/EmptyBlocks";
import LoadingFeedCard from "~/components/feed/card/LoadingFeedCard";
import FeedItem from "~/components/feed/FeedItem";
import { FeedContext } from "~/components/feed/interfaces";

interface IFeedProps {
  targetId?: string;
  targetType?: Types.IConnectTargetType;
  context?: FeedContext;
  feedLimit?: number;
  showNoPosts?: boolean;
}

function Feed({ targetId, targetType, router, context, showNoPosts = true, feedLimit }: IFeedProps & WithRouterProps) {
  const user = useSelfie();
  const groupSlug = get("query.groupSlug", router);
  const isContentTab = get("query.content", router) === "connect";
  const limit = feedLimit || 15;

  return (
    <Query<IConnectQuery, IConnectQueryVariables>
      query={ConnectQuery}
      notifyOnNetworkStatusChange={true}
      variables={{
        targetId: targetId || null,
        targetType: targetType || null,
        limit,
        beforeThisId: null,
        excludeUser: null,
      }}
    >
      {({ data, fetchMore, loading }) => {
        if (
          !loading &&
          data &&
          showNoPosts &&
          data.connect &&
          data.connect.items.length === 0 &&
          (targetType === Types.IConnectTargetType.Group ||
            (targetType === Types.IConnectTargetType.Government &&
              user &&
              user.__typename === "GovernmentUser" &&
              user.government &&
              user.government._id === targetId) ||
            (targetType === Types.IConnectTargetType.Vendor &&
              user &&
              user.__typename === "VendorUser" &&
              user.company &&
              user.company._id === targetId))
        ) {
          return <NoPosts />;
        }

        return (
          <div
            css={{
              display: "grid",
              gridRowGap: 16,
            }}
          >
            {data &&
              data.connect &&
              data.connect.items.map((feedItem) => {
                return <FeedItem key={feedItem._id} feedItem={feedItem} context={context} />;
              })}

            {loading && <LoadingFeedCard />}

            {groupSlug &&
              !isContentTab &&
              !loading &&
              data &&
              data.connect.items.length > 0 &&
              data.connect.items.length < data.connect.totalCount && (
                <Link
                  passHref={true}
                  href="/groups/[groupSlug]"
                  as={`/groups/${groupSlug}?content=connect&feedLimit=15`}
                >
                  <a
                    css={{
                      textTransform: "uppercase",
                      justifySelf: "center",
                      background: "none",
                      outline: "none",
                      border: "none",
                      color: palette.primary,
                      letterSpacing: 1,
                      fontSize: 13,
                      fontWeight: 600,
                      cursor: "pointer",
                    }}
                  >
                    <FormattedMessage defaultMessage="View More" id="QQSdHP" />
                  </a>
                </Link>
              )}

            {(!groupSlug || (groupSlug && isContentTab)) &&
              !loading &&
              data &&
              data.connect.items.length > 0 &&
              data.connect.items.length < data.connect.totalCount && (
                <button
                  type="button"
                  css={{
                    textTransform: "uppercase",
                    justifySelf: "center",
                    background: "none",
                    outline: "none",
                    border: "none",
                    color: palette.primary,
                    letterSpacing: 1,
                    fontSize: 13,
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    if (!data) {
                      return null;
                    }

                    fetchMore({
                      query: ConnectQuery,
                      variables: {
                        targetId: targetId,
                        targetType: targetType,
                        beforeThisId: data.connect.items[data.connect.items.length - 1]._id,
                        limit,
                      },
                      updateQuery: (previousQueryResult: any, { fetchMoreResult }) => {
                        if (fetchMoreResult) {
                          return {
                            ...previousQueryResult,
                            connect: {
                              ...fetchMoreResult.connect,
                              items: [...previousQueryResult.connect.items, ...fetchMoreResult.connect.items],
                            },
                          };
                        }

                        return previousQueryResult;
                      },
                    });
                  }}
                >
                  <FormattedMessage defaultMessage="View More" id="QQSdHP" />
                </button>
              )}
          </div>
        );
      }}
    </Query>
  );
}

export default withRouter(Feed);
